import { Form, useActionData, useNavigation } from 'react-router'
import type { MetaFunction, ActionFunctionArgs } from 'react-router'
import { redirect } from 'react-router'
import { login, createSessionCookie } from '~/lib/auth.server'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { Shield, Mail, Lock } from 'lucide-react'

export const meta: MetaFunction = () => [
  { title: 'Connexion — AfficheServices' },
  { name: 'robots', content: 'noindex, nofollow' },
]

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'auth')) return rateLimitResponse()

  const formData = await request.formData()
  const email = formData.get('email') as string
  const password = formData.get('password') as string

  if (!email || !password) {
    return { error: 'Email et mot de passe requis' }
  }

  const result = await login(email, password)

  if ('error' in result) {
    return { error: result.error }
  }

  const cookie = await createSessionCookie(result.userId, request)
  const redirectTo = result.role === 'professional' ? '/espace-pro' : '/tableau-de-bord'

  return redirect(redirectTo, {
    headers: { 'Set-Cookie': cookie },
  })
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Bon retour !</h1>
      <p className="mt-1 text-sm text-slate-500">
        Connectez-vous pour gérer vos demandes et devis.
      </p>

      <Form method="post" className="mt-8 space-y-4">
        {actionData?.error && (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{actionData.error}</div>
        )}

        <div>
          <label htmlFor="email" className="block text-xs font-medium text-slate-600">Email</label>
          <div className="relative mt-1">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="email" name="email" type="email" autoComplete="email" required
              className="block w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="vous@exemple.fr"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="block text-xs font-medium text-slate-600">Mot de passe</label>
            <a href="/forgot-password" className="text-[11px] font-medium text-primary hover:underline">
              Mot de passe oublié ?
            </a>
          </div>
          <div className="relative mt-1">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="password" name="password" type="password" autoComplete="current-password" required
              className="block w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="••••••••"
            />
          </div>
        </div>

        <button
          type="submit" disabled={isSubmitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-50 btn-press"
        >
          {isSubmitting ? (
            <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Connexion...</>
          ) : 'Se connecter'}
        </button>
      </Form>

      {/* Trust + links */}
      <div className="mt-6 space-y-3">
        <p className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400">
          <Shield className="h-3 w-3 text-emerald-500" /> Connexion sécurisée · Données protégées
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
          <a href="/register" className="font-medium text-primary hover:underline">Créer un compte</a>
          <span className="text-slate-200">|</span>
          <a href="/register-pro" className="font-medium text-primary hover:underline">Inscription Pro</a>
        </div>
      </div>
    </div>
  )
}
